"use client";
import React, { useEffect, useRef, useState } from 'react';
import { usePodcast } from '../context/PodcastContext';

const PodcastPlayer = () => {
    const {
        currentEpisode,
        isPlaying,
        setIsPlaying,
        isPlayerVisible,
        closePlayer,
        togglePlay,
        audioRef
    } = usePodcast();

    const [progress, setProgress] = useState(0);
    const [duration, setDuration] = useState(0);
    const [volume, setVolume] = useState(1);
    const [isMuted, setIsMuted] = useState(false);

    // Get storage key for progress
    const getProgressKey = () => {
        return currentEpisode ? `podcast_progress_${currentEpisode.id}` : null;
    };

    // Load saved progress when episode changes
    useEffect(() => {
        if (currentEpisode) {
            try {
                const savedProgress = localStorage.getItem(getProgressKey());
                if (savedProgress) {
                    const progressData = JSON.parse(savedProgress);
                    setProgress(progressData.progress || 0);
                } else {
                    setProgress(0);
                }
                setDuration(0);
            } catch (error) {
                console.error('Error loading progress:', error);
                setProgress(0);
            }
        }
    }, [currentEpisode?.id]);

    // Save progress periodically (every 2 seconds)
    useEffect(() => {
        if (!currentEpisode || !isPlayerVisible) return;

        const progressKey = getProgressKey();
        if (!progressKey) return;

        const saveProgress = () => {
            try {
                const progressData = {
                    progress: progress,
                    timestamp: Date.now()
                };
                localStorage.setItem(progressKey, JSON.stringify(progressData));
            } catch (error) {
                console.error('Error saving progress:', error);
            }
        };

        const interval = setInterval(saveProgress, 2000); // Save every 2 seconds

        return () => clearInterval(interval);
    }, [progress, currentEpisode?.id, isPlayerVisible]);

    // Note: Progress is cleared in closePlayer function, not here
    // This allows progress to persist if user navigates away without closing player

    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;

        if (currentEpisode) {
            // If source changes, load it
            if (audio.src !== currentEpisode.audioUrl) {
                audio.src = currentEpisode.audioUrl;
                audio.load();
                
                // Restore saved progress after metadata loads
                const restoreProgress = () => {
                    try {
                        if (currentEpisode) {
                            const progressKey = `podcast_progress_${currentEpisode.id}`;
                            const savedProgress = localStorage.getItem(progressKey);
                            if (savedProgress) {
                                const progressData = JSON.parse(savedProgress);
                                if (progressData.progress && audio.duration) {
                                    // Only restore if progress is valid and less than duration
                                    const savedTime = Math.min(progressData.progress, audio.duration - 1);
                                    audio.currentTime = savedTime;
                                    setProgress(savedTime);
                                }
                            }
                        }
                    } catch (error) {
                        console.error('Error restoring progress:', error);
                    }
                };

                audio.addEventListener('loadedmetadata', restoreProgress, { once: true });
                audio.addEventListener('canplay', restoreProgress, { once: true });
            }

            if (isPlaying) {
                audio.play().catch(e => {
                    console.error("Audio play failed:", e);
                    setIsPlaying(false);
                });
            } else {
                audio.pause();
            }
        }
    }, [currentEpisode, isPlaying, audioRef, setIsPlaying]);

    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;

        const updateTime = () => {
            if (audio.currentTime && !isNaN(audio.currentTime)) {
                setProgress(audio.currentTime);
            }
        };

        const updateDuration = () => {
            if (audio.duration && !isNaN(audio.duration) && isFinite(audio.duration)) {
                setDuration(audio.duration);
            }
        };

        const onEnded = () => {
            setIsPlaying(false);
            setProgress(0);
        };

        // Listen to multiple events to ensure duration is captured
        audio.addEventListener('timeupdate', updateTime);
        audio.addEventListener('loadedmetadata', updateDuration);
        audio.addEventListener('loadeddata', updateDuration);
        audio.addEventListener('canplay', updateDuration);
        audio.addEventListener('durationchange', updateDuration);
        audio.addEventListener('ended', onEnded);

        // Try to get duration immediately if available
        if (audio.duration && !isNaN(audio.duration) && isFinite(audio.duration)) {
            setDuration(audio.duration);
        }

        return () => {
            audio.removeEventListener('timeupdate', updateTime);
            audio.removeEventListener('loadedmetadata', updateDuration);
            audio.removeEventListener('loadeddata', updateDuration);
            audio.removeEventListener('canplay', updateDuration);
            audio.removeEventListener('durationchange', updateDuration);
            audio.removeEventListener('ended', onEnded);
        };
    }, [audioRef, currentEpisode?.id]);

    const handleSeek = (e) => {
        const audio = audioRef.current;
        if (!audio) return;
        
        const newTime = parseFloat(e.target.value);
        if (!isNaN(newTime) && isFinite(newTime)) {
            audio.currentTime = newTime;
            setProgress(newTime);
        }
    };

    const formatTime = (time) => {
        if (!time || isNaN(time) || !isFinite(time)) return '00:00';
        const minutes = Math.floor(time / 60);
        const seconds = Math.floor(time % 60);
        // Handle RTL by respecting LTR format for digits
        return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
    };

    const toggleMute = () => {
        if (audioRef.current) {
            audioRef.current.muted = !isMuted;
            setIsMuted(!isMuted);
        }
    };

    if (!isPlayerVisible || !currentEpisode) return null;

    return (
        <div className={`podcast-player-container position-fixed text-white ${isPlayerVisible ? 'd-block' : 'd-none'}`}>

            <div className="container py-2" dir="rtl">
                <div className="row align-items-center">

                    {/* Episode Info */}
                    <div className="col-12 col-md-4 d-flex align-items-center mb-2 mb-md-0 episode-info">
                        <div className="position-relative flex-shrink-0" style={{ width: '50px', height: '50px' }}>
                            {currentEpisode.featured_image_urls?.thumbnail ? (
                                <img
                                    src={currentEpisode.featured_image_urls.thumbnail}
                                    alt="Episode Art"
                                    className="w-100 h-100 object-fit-cover rounded"
                                />
                            ) : (
                                <div className="w-100 h-100 bg-secondary rounded d-flex align-items-center justify-content-center">
                                    <i className="bi bi-mic text-white"></i>
                                </div>
                            )}
                        </div>
                        <div className="ms-3 overflow-hidden text-start">
                            <h6 className="mb-0 text-truncate small fw-bold text-white" title={currentEpisode.title.rendered}>
                                {currentEpisode.title.rendered}
                            </h6>
                            <small className="text-secondary text-truncate d-block">
                                {currentEpisode.author}
                            </small>
                        </div>
                    </div>

                    {/* Controls */}
                    <div className="col-12 col-md-6 mb-2 mb-md-0" dir="ltr">
                        <div className="d-flex flex-column align-items-center">
                            <div className="d-flex align-items-center gap-3 mb-1 player-controls">
                                <button className="btn btn-link text-secondary p-0 border-0" onClick={() => { audioRef.current.currentTime += 10 }}>
                                    <i className="bi bi-arrow-counterclockwise fs-5"></i>
                                </button>

                                <button className="btn btn-light rounded-circle d-flex align-items-center justify-content-center p-0"
                                    style={{ width: '40px', height: '40px' }}
                                    onClick={togglePlay}>
                                    <i className={`bi ${isPlaying ? 'bi-pause-fill' : 'bi-play-fill flip-hh'} fs-4 text-dark`}></i>
                                </button>

                                 <button className="btn btn-link text-secondary p-0 border-0  " onClick={() => { audioRef.current.currentTime -= 10 }}>
                                    <i className="bi bi-arrow-clockwise fs-5"></i>
                                </button>
                            </div>

                            <div className="w-100 d-flex align-items-center gap-2 mt-2 player-time-container" style={{ fontSize: '0.75rem' , position: 'relative' }}>
                            <span className="text-secondary audio-time-left">{formatTime(duration)}</span>

                                <input
                                    type="range"
                                    className=" flex-grow-1"
                                    min="0"
                                    max={duration && !isNaN(duration) && isFinite(duration) ? duration : 100}
                                    value={progress && !isNaN(progress) ? progress : 0}
                                    onChange={handleSeek}
                                    onInput={handleSeek}
                                    style={{ 
                                        height: '4px', 
                                        cursor: 'pointer',
                                        direction: 'rtl'
                                    }}
                                    disabled={!duration || duration === 0}
                                />
                                <span className="text-secondary audio-time-current">{formatTime(progress)}</span>

                            </div>
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="PodcastPlayerActions col-12 col-md-4 d-flex align-items-center justify-content-end gap-3" dir="ltr">
                        <button className="btn btn-link text-secondary p-0 d-md-block flip-h border-0" onClick={toggleMute}>
                            <i className={`bi ${isMuted ? 'bi-volume-mute' : 'bi-volume-up'} fs-2`}></i>
                        </button>

                        <button className="btn text-danger p-0 ms-3 border-0" onClick={closePlayer} title="إغلاق">
                            <span className="fs-3 text-danger"> ❌ </span>
                        </button>
                    </div>
                </div>
            </div>

            {/* Hidden Audio Element */}
            <audio ref={audioRef} preload="metadata" />
        </div>
    );
};

export default PodcastPlayer;
